import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { Camera, RefreshCw, Loader2, CheckCircle2, ShieldCheck } from "lucide-react";

export const FaceSignCapture = ({ tokenId, token, onSigned, onCancel, onSubmit }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [camError, setCamError] = useState("");
  const [selfie, setSelfie] = useState(null);
  const [consent, setConsent] = useState(false);
  const [signing, setSigning] = useState(false);

  const stopCam = useCallback(() => {
    streamRef.current?.getTracks()?.forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const startCam = useCallback(async () => {
    setCamError("");
    setReady(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 640 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        try {
          await videoRef.current.play();
        } catch (playErr) {
          if (playErr?.name === "AbortError") return; // StrictMode re-run
          throw playErr;
        }
      }
      setCamError("");
      setReady(true);
    } catch (e) {
      if (e?.name === "AbortError") return;
      setCamError(
        "Não foi possível acessar a câmera. Verifique a permissão do navegador."
      );
    }
  }, []);

  useEffect(() => {
    if (!selfie) startCam();
    return stopCam;
  }, [selfie, startCam, stopCam]);

  const capture = () => {
    const video = videoRef.current;
    if (!video) return;
    const size = 560;
    const canvas = document.createElement("canvas");
    const vw = video.videoWidth || size;
    const vh = video.videoHeight || size;
    const s = Math.min(vw, vh);
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, (vw - s) / 2, (vh - s) / 2, s, s, 0, 0, size, size);
    setSelfie(canvas.toDataURL("image/jpeg", 0.85));
    stopCam();
  };

  const submit = async () => {
    setSigning(true);
    try {
      if (onSubmit) {
        await onSubmit(selfie);
      } else {
        const r = await axios.post(
          `${API}/employee/attendances/${tokenId}/sign-face`,
          { selfie_base64: selfie, consent: true },
          { params: { token } }
        );
        toast.success(
          r.data?.face_status === "ok"
            ? "Rosto verificado — presença assinada com sucesso!"
            : "Presença assinada! Sua foto será conferida pela administração."
        );
        onSigned();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao assinar com o rosto");
    } finally {
      setSigning(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="face-sign-capture">
      <div className="rounded-xl overflow-hidden bg-slate-900 relative mx-auto"
        style={{ width: "min(100%, 320px)", aspectRatio: "1 / 1" }}>
        {selfie ? (
          <img src={selfie} alt="Sua selfie" className="w-full h-full object-cover" />
        ) : (
          <video
            ref={videoRef}
            playsInline
            muted
            className="w-full h-full object-cover scale-x-[-1]"
          />
        )}
        {!selfie && !ready && !camError && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-white/70" />
          </div>
        )}
        {!selfie && (
          <div className="absolute inset-0 pointer-events-none border-[3px] border-white/40 rounded-xl m-8 rounded-[50%]" />
        )}
      </div>

      {camError && (
        <div className="text-xs text-red-600 text-center" data-testid="face-cam-error">
          {camError}
        </div>
      )}

      {!selfie ? (
        <div className="flex justify-center gap-2">
          <Button variant="ghost" onClick={onCancel}>Voltar</Button>
          <Button
            onClick={capture}
            disabled={!ready}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="face-capture-btn"
          >
            <Camera className="w-4 h-4 mr-1.5" /> Tirar foto
          </Button>
        </div>
      ) : (
        <>
          <div className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-2.5">
            <Checkbox
              id="face-consent"
              checked={consent}
              onCheckedChange={setConsent}
              data-testid="face-consent-checkbox"
            />
            <Label htmlFor="face-consent" className="text-[11px] leading-snug text-slate-700">
              Autorizo o uso da minha imagem facial para autenticar esta
              assinatura, conforme a LGPD (Lei 13.709/2018). A foto ficará
              registrada junto à assinatura para fins de auditoria.
            </Label>
          </div>
          <div className="flex justify-center gap-2">
            <Button variant="outline" onClick={() => setSelfie(null)} disabled={signing}>
              <RefreshCw className="w-4 h-4 mr-1.5" /> Tirar outra
            </Button>
            <Button
              onClick={submit}
              disabled={!consent || signing}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="face-submit-btn"
            >
              {signing
                ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
              Confirmar e assinar
            </Button>
          </div>
          <div className="text-[10px] text-slate-500 text-center flex items-center justify-center gap-1">
            <ShieldCheck className="w-3 h-3" />
            Seu rosto será comparado com a foto do seu cadastro
          </div>
        </>
      )}
    </div>
  );
};
